import { computed, inject, provide, ref, type ComputedRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { OBreadcrumbs, SidebarConfig } from '@ai/ui-components'
import type { PropsOf } from '@/types/typeUtils'

type BreadcrumbItem = PropsOf<typeof OBreadcrumbs>['items'][number]
const DYNAMIC_BREADCRUMB_KEY = Symbol('dynamicBreadcrumb')

export function useBreadcrumbs(sidebarConfigRef: ComputedRef<SidebarConfig>) {
  const route = useRoute()
  const { t } = useI18n({ useScope: 'global' })

  const createBreadcrumbLink = (path: string): Pick<BreadcrumbItem, 'href' | 'to'> => ({
    href: path,
    to: path,
  })

  // Create a reactive ref for dynamic breadcrumb that child components can update
  const dynamicBreadcrumb = ref<string | null>(null)

  // Provide it so child components can inject and update it
  provide(DYNAMIC_BREADCRUMB_KEY, dynamicBreadcrumb)

  const breadcrumbs = computed<BreadcrumbItem[]>(() => {
    if (route.meta.breadcrumbs === false) {
      return []
    }

    const sidebarConfig = sidebarConfigRef.value
    const items: BreadcrumbItem[] = []

    // Always add Home as the first breadcrumb
    const locale = route.params.locale as string

    // Get the current path without locale
    const pathWithoutLocale = route.path.replace(`/${locale}`, '').replace(/^\//, '')

    // Split the path into segments
    const segments = pathWithoutLocale.split('/')
    const firstSegment = segments[0]

    // Search for the matching item in the sidebar config
    for (const section of sidebarConfig) {
      if (section.type === 'static') {
        // Check items in static sections
        for (const item of section.items) {
          if (item.path === firstSegment) {
            items.push({
              label: t(item.labelKey),
            })
            // Check for additional segments (tabs/sub-pages)
            if (segments.length > 1 && route.meta.breadcrumbLabel) {
              items.push({
                label:
                  typeof route.meta.breadcrumbLabel === 'string'
                    ? t(route.meta.breadcrumbLabel)
                    : String(route.meta.breadcrumbLabel),
              })
            }
            return items
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (section.type === 'collapsible' || section.type === 'footer') {
        // Check items in collapsible/footer sections
        for (const item of section.items) {
          // Skip external links
          if ('external' in item && item.external) continue

          if (item.path === firstSegment) {
            // Add the parent group label
            if (section.labelKey) {
              items.push({
                label: t(section.labelKey),
              })
            }
            // Add the current item label
            items.push({
              label: t(item.labelKey),
              ...(segments.length > 1 || dynamicBreadcrumb.value
                ? createBreadcrumbLink(`/${locale}/${firstSegment}`)
                : {}),
            })
            // Check for additional segments (tabs/sub-pages) from route meta
            if (segments.length > 1 && route.meta.breadcrumbLabel) {
              items.push({
                label:
                  typeof route.meta.breadcrumbLabel === 'string'
                    ? t(route.meta.breadcrumbLabel)
                    : String(route.meta.breadcrumbLabel),
              })
            }
            // Check for dynamic breadcrumb from child component
            else if (dynamicBreadcrumb.value) {
              items.push({
                label: t(dynamicBreadcrumb.value),
              })
            }
            return items
          }
        }
      }
    }

    // Handle special cases for nested routes that don't match sidebar items
    // For example: /configurator/:id should show Build > Virtual Agent > Configurator
    if (firstSegment === 'configurator') {
      // Find Virtual Agents in the sidebar
      for (const section of sidebarConfig) {
        if (section.type === 'collapsible' || section.type === 'footer') {
          for (const item of section.items) {
            if (item.path === 'virtual-agents') {
              if (section.labelKey) {
                items.push({
                  label: t(section.labelKey),
                  to: undefined,
                })
              }
              items.push({
                label: t(item.labelKey),
                ...createBreadcrumbLink(`/${locale}/virtual-agents`),
              })
              // Use the declared route parameter instead of URL segment position because
              // create routes include the active tab as their second segment.
              const hasId = Boolean(route.params.id)
              const isDuplicate = route.query.duplicate === 'true'
              items.push({
                label: hasId && !isDuplicate ? t('edit') : t('create'),
              })
              return items
            }
          }
        }
      }
    }

    // If no match found, just show the path segment as a fallback
    items.push({
      label: firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1).replace(/-/g, ' '),
    })

    return items
  })

  return {
    breadcrumbs,
  }
}

/**
 * Hook for child components to set a dynamic breadcrumb label
 * This allows components with tabs to update the breadcrumb based on the active tab
 */
export function useDynamicBreadcrumb() {
  const dynamicBreadcrumb = inject<Ref<string | null>>(DYNAMIC_BREADCRUMB_KEY)

  if (!dynamicBreadcrumb) {
    console.warn(
      'useDynamicBreadcrumb must be used within a component tree that provides breadcrumb context',
    )
  }

  return {
    setDynamicBreadcrumb: (label: string | null) => {
      if (dynamicBreadcrumb) {
        dynamicBreadcrumb.value = label
      }
    },
  }
}
